import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { motion } from 'motion/react';
import { ArrowLeft, LayoutDashboard, LogOut, Moon, Search, Sun, ChevronDown,} from 'lucide-react';
import { groupedNavItemsFor } from '@/lib/nav';
import { NAV_ICONS } from '@/lib/nav-icons';
import { useClerk } from '@clerk/clerk-react';
import { useMe } from '@/lib/use-me';
import { useTheme } from '@/lib/theme';
import { useAuthConfig } from '@/App';
import { api } from '@/lib/api';
import { AssistantWidget } from '@/components/assistant-widget';
import { AlertBell } from '@/components/alert-bell';
import { clerkEnabled } from '@/lib/clerk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

function LogoutMenuItem({ onLogout }: { onLogout: () => Promise<void> }) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={onLogout}
          tooltip="Sign out"
          className="text-sidebar-muted hover:text-sidebar-accent-foreground"
        >
          <LogOut className="size-4" />
          <span>Sign out</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Clerk mode: the session lives with Clerk, so sign-out must go through it —
 *  clearing local state alone leaves the session cookie valid. */
function ClerkLogoutButton() {
  const { signOut } = useClerk();
  const qc = useQueryClient();
  return (
    <LogoutMenuItem
      onLogout={async () => {
        qc.clear();
        await signOut({ redirectUrl: '/' });
      }}
    />
  );
}

function LocalLogoutButton() {
  const { data: config } = useAuthConfig();
  const qc = useQueryClient();
  return (
    <LogoutMenuItem
      onLogout={async () => {
        if (config?.mode === 'local') {
          await api.post('/api/auth/logout');
        }
        qc.clear();
        window.location.reload();
      }}
    />
  );
}

export function LogoutButton() {
  return clerkEnabled ? <ClerkLogoutButton /> : <LocalLogoutButton />;
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button variant="ghost" size="icon-sm" onClick={toggle} aria-label="Toggle theme">
      {/* Icon swap rotates in on theme change; respects reduced motion via MotionConfig */}
      <motion.span
        key={theme}
        initial={{ opacity: 0, rotate: -40, scale: 0.7 }}
        animate={{ opacity: 1, rotate: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="flex"
        aria-hidden="true"
      >
        {theme === 'dark' ? <Moon className="size-[18px]" /> : <Sun className="size-[18px]" />}
      </motion.span>
    </Button>
  );
}

function initials(name?: string) {
  if (!name) return '?';
  return name.slice(0, 2).toUpperCase();
}

export function Layout({ children }: { children: ReactNode }) {
  const { data: me, isLoading, error } = useMe();
  const [location] = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (error || !me) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">Not signed in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Could not load your session. Check the server is running and you are authenticated.
          </p>
        </div>
      </div>
    );
  }

  const groups = groupedNavItemsFor(me.role);
  // Live sidebar infographics: my open tasks, overdue invoices, open leads,
  // and a breach dot on Dashboard. Polled every minute; keys are role-trimmed
  // server-side so hidden modules never leak counts.
  const { data: badges } = useQuery({
    queryKey: ['nav', 'badges'],
    queryFn: () => api.get<Record<string, number>>('/api/config/nav-badges'),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });
  const badgeFor = (path: string): number | undefined => {
    if (!badges) return undefined;
    if (path === '/tasks') return badges.tasks || undefined;
    if (path === '/invoices') return badges.invoicesOverdue || undefined;
    if (path === '/leads') return badges.leadsOpen || undefined;
    return undefined;
  };
  // Collapsible nav groups — persisted per browser; the group holding the
  // current page auto-expands so the active item is never hidden.
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('nav.collapsed.groups') ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev) => {
      const next = prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group];
      localStorage.setItem('nav.collapsed.groups', JSON.stringify(next));
      return next;
    });
  };
  // Resolve the nav item by longest prefix so sub-pages (/projects/5/edit)
  // still map to their section; the tail becomes the last breadcrumb.
  const current = groups
    .flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })))
    .filter((i) =>
      i.path === '/' ? location === '/' : location === i.path || location.startsWith(`${i.path}/`),
    )
    .sort((a, b) => b.path.length - a.path.length)[0];
  const subLabel = (() => {
    if (!current || location === current.path) return null;
    const rest = location.slice(current.path.length + 1);
    if (rest === 'new') return 'New';
    if (rest.endsWith('/edit')) return 'Edit';
    const seg = rest.split('/')[0]!;
    return seg.charAt(0).toUpperCase() + seg.slice(1);
  })();

  return (
    <SidebarProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to content
      </a>
      <Sidebar collapsible="icon">
        <SidebarHeader className="px-3 pt-4 pb-2">
          {/* overflow-hidden clips the wordmark as the rail narrows; the logo mark (12px inset in a 48px rail) stays optically centered */}
          <div className="flex items-center gap-2.5 overflow-hidden">
            <img
              src="/logo.png"
              alt="AxionCRM"
              className="size-6 shrink-0 rounded-[5px] object-cover"
            />
            <span className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap transition-opacity duration-200 group-data-[collapsible=icon]:opacity-0">
              <span className="text-sm font-semibold tracking-[-0.01em] text-sidebar-accent-foreground">
                Axion<span className="font-normal text-sidebar-muted">CRM</span>
              </span>
              <span className="font-mono text-[10px] text-sidebar-muted" title={`AxionCRM v${__APP_VERSION__}`}>
                v{__APP_VERSION__}
              </span>
            </span>
          </div>
        </SidebarHeader>
        {/* px-0 in icon mode so the 32px buttons sit centered in the 48px rail */}
        <SidebarContent className="px-1 pt-2 group-data-[collapsible=icon]:px-0">
          {groups.map(({ group, items }) => {
            const activeInGroup = items.some((i) =>
              i.path === '/' ? location === '/' : location === i.path || location.startsWith(`${i.path}/`),
            );
            const isCollapsed =
              group !== 'Overview' && collapsedGroups.includes(group) && !activeInGroup;
            return (
            <SidebarGroup key={group} className="py-1.5">
              {group !== 'Overview' && (
                <SidebarGroupLabel
                  className="h-7 p-0 text-[10.5px] font-medium tracking-[0.1em] uppercase text-sidebar-muted group-data-[collapsible=icon]:-mt-7"
                  render={
                    <button
                      type="button"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(group)}
                      className="flex w-full cursor-pointer items-center justify-between rounded-md px-2 hover:bg-sidebar-accent/40 hover:text-sidebar-accent-foreground"
                    />
                  }
                >
                  <span className="flex items-center gap-1.5">
                    {group}
                    {isCollapsed && (
                      <span className="rounded-full bg-sidebar-accent/60 px-1.5 text-[9px] font-semibold normal-case tracking-normal">
                        {items.length}
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    className={`size-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    aria-hidden="true"
                  />
                </SidebarGroupLabel>
              )}
              {/* In the icon rail the labels are hidden, so collapse only
                  applies at full width — icons always stay reachable. */}
              <SidebarGroupContent
                className={isCollapsed ? 'hidden group-data-[collapsible=icon]:block' : undefined}
              >
                <SidebarMenu className="gap-1">
                  {items.map((item) => {
                    const Icon = NAV_ICONS[item.path] ?? LayoutDashboard;
                    const active = location === item.path;
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={active}
                          tooltip={item.label}
                          className="relative h-8 gap-2.5 px-2 text-[13px] text-sidebar-muted transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground active:bg-sidebar-accent/60 data-active:text-sidebar-accent-foreground data-active:hover:bg-sidebar-accent data-active:[&_svg]:text-sidebar-primary"
                          render={
                            <Link
                              href={item.path}
                              aria-current={active ? 'page' : undefined}
                              // Explicit accessible name: the visible label is hidden at icon width
                              aria-label={item.label}
                            />
                          }
                        >
                          {active && (
                            /* Shared-layout accent bar: springs to the newly active item */
                            <motion.span
                              layoutId="sidebar-active-bar"
                              transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                              className="absolute top-1.5 bottom-1.5 left-0 w-[2px] rounded-full bg-sidebar-primary"
                              aria-hidden="true"
                            />
                          )}
                          <Icon />
                          {/* Fades (rather than pops) while the rail width animates */}
                          <span className="transition-opacity duration-200 group-data-[collapsible=icon]:opacity-0">
                            {item.label}
                          </span>
                          {/* Trailing adornments: ml-auto only works after the label */}
                          {badgeFor(item.path) !== undefined && (
                            <span className="ml-auto rounded-full bg-sidebar-accent px-1.5 text-[10px] font-semibold tabular-nums text-sidebar-accent-foreground group-data-[collapsible=icon]:hidden">
                              {badgeFor(item.path)}
                            </span>
                          )}
                          {item.path === '/' && (badges?.breaches ?? 0) > 0 && (
                            <span
                              className="ml-auto size-2 rounded-full bg-destructive group-data-[collapsible=icon]:hidden"
                              title={`${badges!.breaches} guardrail breach(es)`}
                              aria-label={`${badges!.breaches} guardrail breaches`}
                            />
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
          })}
        </SidebarContent>
        {/* p-2 in icon mode centers the 32px avatar in the 48px rail; name/role fade + clip */}
        <SidebarFooter className="border-t border-sidebar-border p-3 group-data-[collapsible=icon]:p-2">
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-primary">
              {initials(me.firstName)}
            </div>
            <div className="min-w-0 flex-1 transition-opacity duration-200 group-data-[collapsible=icon]:opacity-0">
              <div className="truncate text-[13px] font-medium text-sidebar-accent-foreground">
                {me.firstName}
              </div>
              <div className="truncate text-[11px] capitalize text-sidebar-muted">{me.role}</div>
            </div>
            <LogoutButton />
          </div>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-12 items-center justify-between gap-2 border-b border-border bg-background/85 px-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-2">
            <SidebarTrigger />
            {location !== '/' && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => window.history.back()}
                aria-label="Go back"
                title="Go back"
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <Separator orientation="vertical" className="h-4" />
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-[13px]">
              <Link
                href="/"
                className="hidden text-muted-foreground transition-colors hover:text-foreground sm:inline"
              >
                Home
              </Link>
              <span className="hidden text-border sm:inline" aria-hidden="true">
                /
              </span>
              {current && current.group !== 'Overview' && (
                <>
                  <span className="hidden text-muted-foreground sm:inline">{current.group}</span>
                  <span className="hidden text-border sm:inline" aria-hidden="true">
                    /
                  </span>
                </>
              )}
              {current && subLabel ? (
                <>
                  <Link
                    href={current.path}
                    className="truncate text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {current.label}
                  </Link>
                  <span className="text-border" aria-hidden="true">
                    /
                  </span>
                  <span className="truncate font-medium text-foreground" aria-current="page">
                    {subLabel}
                  </span>
                </>
              ) : (
                <span className="truncate font-medium text-foreground" aria-current="page">
                  {current?.label ?? 'AxionCRM'}
                </span>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() =>
                document.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'k', metaKey: true }),
                )
              }
              aria-label="Search pages or run a command"
              className="flex h-7 items-center gap-2 rounded-md border border-border bg-card px-2.5 text-xs text-muted-foreground transition-colors hover:border-input hover:text-foreground"
            >
              <Search className="size-3.5" />
              <span className="hidden sm:inline" aria-hidden="true">
                Search…
              </span>
              <kbd
                className="ml-2 hidden font-mono text-[10px] font-medium text-muted-foreground sm:inline"
                aria-hidden="true"
              >
                ⌘K
              </kbd>
            </button>
            <AlertBell />
            <ThemeToggle />
          </div>
        </header>
        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 outline-none">
          <div className="mx-auto w-full max-w-[1600px] px-6 py-6">{children}
          <AssistantWidget /></div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
